import { parsePresentationDate } from '@/utils/datePresentation';
import { findOptionByRawValue, resolveFirstPositiveOptionId } from '@/utils/optionValues';
import {
  normalizeExpenseFeeFirstInstallmentValue,
  normalizeExpenseFeeFrequencyValue,
} from '@/composables/expensesFees/shared/useExpenseFeeModalHydrationShared';

const toSourceData = (initialData = null) => {
  const source = initialData && typeof initialData === 'object' ? initialData : {};
  return source?.data && typeof source.data === 'object' ? source.data : source;
};

export const resolvePositiveExpenseId = (...candidates) => {
  return resolveFirstPositiveOptionId(...candidates);
};

export const normalizeExpenseFrequencyValue = (rawFrequency) => {
  return normalizeExpenseFeeFrequencyValue(rawFrequency);
};

export const normalizeExpenseFirstInstallmentValue = (rawValue) => {
  return normalizeExpenseFeeFirstInstallmentValue(rawValue);
};

export const hydrateExpenseFormFromInitialData = ({
  initialData = null,
  form = {},
  options = {},
  firstInstallmentOptions = [],
  applyTaxSelectionFromDetail = () => {},
}) => {
  const data = toSourceData(initialData);

  const voucherType = String(data.voucher_type || data.voucherType || '').trim().toLowerCase();
  if (voucherType !== '') {
    form.voucherType = voucherType;
  }

  const expenseStatus = String(data.expense_status || data.status || '').trim().toLowerCase();
  if (expenseStatus !== '') {
    form.expenseStatus = expenseStatus;
  }

  if (Object.prototype.hasOwnProperty.call(data, 'is_approved')) {
    form.isApproved = Number(data.is_approved) === 1;
  }

  const currencyCode = String(data.currency || data.currencyCode || '').trim().toUpperCase();
  if (currencyCode !== '') {
    form.currencyCode = currencyCode;
  }

  form.exchangeRate = Number(data.exchange_rate || data.type_change || form.exchangeRate || 1) || 1;
  form.amount = Number(data.amount || 0) || 0;
  form.discount = Number(data.discount || 0) || 0;
  form.deductions = Number(data.deductions || 0) || 0;

  form.supplier = findOptionByRawValue(
    options.suppliers,
    [data.supplier, data.provider, data.supplier_id, data.provider_id]
  );

  const expenseClassId = Number(data.class_id || data.class?.id || 0);
  form.expenseClass = expenseClassId > 0
    ? {
      value: expenseClassId,
      label: String(data.class?.name || '').trim() || `Clase #${expenseClassId}`,
    }
    : null;

  const categoryId = Number(data.category_id || data.category?.id || 0);
  form.expenseCategory = categoryId > 0
    ? {
      value: categoryId,
      label: String(data.category?.name || '').trim() || `Categoria #${categoryId}`,
    }
    : null;

  form.customer = findOptionByRawValue(
    options.customers,
    [data.company, data.customer, data.company_id, data.customer_id]
  );

  form.sale = findOptionByRawValue(
    options.sales,
    [data.sale, data.sale_id],
    { metaKeys: ['sale_id'] }
  );

  form.area = findOptionByRawValue(
    options.areas,
    [data.area, data.area_id, data.supplier?.area_id]
  );

  applyTaxSelectionFromDetail(data);

  form.localTaxes = Array.isArray(data.local_taxes)
    ? data.local_taxes.map((item) => ({
      type: String(item?.type || 'trasladado').trim().toLowerCase(),
      name: String(item?.name || '').trim(),
      percentage: Number(item?.percentage || 0) || 0,
      amount: Number(item?.amount || 0) || 0,
    }))
    : [];

  const frequency = normalizeExpenseFrequencyValue(data.frequency);
  if (frequency) {
    form.frequency = frequency;
  }

  form.expensesCount = Math.max(1, Number(data.expenses_count || data.expensesCount || 1) || 1);

  const firstInstallment = normalizeExpenseFirstInstallmentValue(data.first_installment || data.first_month);
  if (firstInstallment !== '') {
    form.firstInstallmentOption = findOptionByRawValue(firstInstallmentOptions, firstInstallment);
  }

  const periodicityCode = String(data.periodicity_code || '').trim().toLowerCase();
  if (periodicityCode !== '') {
    form.periodicityOption = findOptionByRawValue(options.periodicities, periodicityCode);
  }

  form.folio = String(data.folio || '').trim();
  form.concept = String(data.concept || '').trim();
  form.comments = String(data.comments || '').trim();

  const expenseDate = parsePresentationDate(data.date);
  if (expenseDate) {
    form.expenseDate = expenseDate;
  }

  const paymentDate = parsePresentationDate(data.payment_date);
  if (paymentDate) {
    form.paymentDate = paymentDate;
  }

  const cfdiDate = parsePresentationDate(data.cfdi_date || data.invoice_date);
  if (cfdiDate) {
    form.cfdiDate = cfdiDate;
  }

  const rawTags = Array.isArray(data.tags)
    ? data.tags
    : (Array.isArray(data.tag_ids) ? data.tag_ids : []);
  form.tags = rawTags
    .map((tag) => findOptionByRawValue(options.tags, tag))
    .filter(Boolean);

  return data;
};

export const applyExpenseEditPayloadFallbacks = ({
  payload = {},
  isEditMode = false,
  initialData = null,
}) => {
  if (!isEditMode || !payload || typeof payload !== 'object') {
    return payload;
  }

  const source = toSourceData(initialData);
  const nextPayload = { ...payload };

  nextPayload.supplier_id = Number(nextPayload.supplier_id || 0) > 0
    ? Number(nextPayload.supplier_id || 0)
    : resolvePositiveExpenseId(source.supplier_id, source.provider_id, source.supplier, source.provider);

  nextPayload.class_id = Number(nextPayload.class_id || 0) > 0
    ? Number(nextPayload.class_id || 0)
    : resolvePositiveExpenseId(source.class_id, source.class);

  nextPayload.category_id = Number(nextPayload.category_id || 0) > 0
    ? Number(nextPayload.category_id || 0)
    : resolvePositiveExpenseId(source.category_id, source.category);

  nextPayload.sale_id = Number(nextPayload.sale_id || 0) > 0
    ? Number(nextPayload.sale_id || 0)
    : resolvePositiveExpenseId(source.sale_id, source.sale);

  if (String(nextPayload.concept || '').trim() === '') {
    nextPayload.concept = String(source.concept || '').trim();
  }

  return nextPayload;
};
